<script setup lang="ts">
import { computed, ref } from 'vue'
import { useHead } from '@unhead/vue'
import type { Category, Resolved } from '@/types'
import { PATHS } from '@/content'
import { useBlogStore, fetchBlogList, type BlogList } from '@/lib/blog'
import { baseHead, breadcrumbLd } from '@/lib/seo'
import ArticleCard from '@/components/ArticleCard.vue'
import BreadcrumbBar from '@/components/BreadcrumbBar.vue'
import CtaProjet from '@/components/CtaProjet.vue'

const props = defineProps<{ resolved: Resolved }>()
const category = computed(() => props.resolved.category as Category)

// Articles de la categorie servis en direct : premiere page avec la page, suivantes a la demande.
const blog = useBlogStore()
const listing = ref<BlogList>(
  blog.payload?.list || {
    items: [],
    total: 0,
    page: 1,
    perPage: 12,
    categoryId: category.value.wp_id,
  },
)
const list = computed(() => listing.value.items)
const page = computed(() => listing.value.page)
const totalPages = computed(() =>
  Math.max(1, Math.ceil(listing.value.total / listing.value.perPage)),
)
const paged = computed(() => listing.value.items)
let seq = 0
async function goPage(p: number) {
  const mine = ++seq
  const target = Math.min(Math.max(1, p), totalPages.value)
  if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
  try {
    const next = await fetchBlogList(target, category.value.wp_id)
    if (mine === seq) listing.value = next
  } catch {
    /* page courante conservee */
  }
}

const crumbs = computed(() => [
  { label: 'Accueil', path: '/' },
  { label: 'Actualités', path: PATHS.blog },
  { label: category.value.name, path: category.value.path },
])

useHead(() => {
  const c = category.value
  return {
    ...baseHead({
      title: `${c.name} : articles et conseils - Les Ateliers du Terras`,
      description:
        c.description ||
        `Tous nos articles et conseils sur le thème ${c.name} à Mayenne, Laval et La Baule.`,
      canonical: c.path,
    }),
    script: [breadcrumbLd(crumbs.value)],
  }
})
</script>

<template>
  <div class="cat">
    <section class="cat-hero">
      <div class="container">
        <span class="eyebrow">Catégorie</span>
        <h1>{{ category.name }}</h1>
        <p v-if="category.description">{{ category.description }}</p>
      </div>
    </section>

    <BreadcrumbBar :crumbs="crumbs" />

    <section class="section">
      <div class="container">
        <template v-if="list.length">
          <div class="grid grid--3">
            <ArticleCard v-for="a in paged" :key="a.path" :article="a" />
          </div>
          <nav v-if="totalPages > 1" class="pager" aria-label="Pagination">
            <button class="pager__btn" :disabled="page === 1" @click="goPage(page - 1)">Précédent</button>
            <span class="pager__info">Page {{ page }} sur {{ totalPages }}</span>
            <button class="pager__btn" :disabled="page === totalPages" @click="goPage(page + 1)">Suivant</button>
          </nav>
        </template>
        <div v-else class="empty">
          <p>De nouveaux articles sur ce thème arrivent prochainement.</p>
          <router-link :to="PATHS.blog" class="btn btn--outline">Voir tous les articles</router-link>
        </div>
      </div>
    </section>

    <CtaProjet />
  </div>
</template>

<style scoped>
.cat-hero {
  background: var(--bg-alt);
  border-bottom: 1px solid var(--border);
  padding: 44px 0;
}
.cat-hero p {
  max-width: 720px;
  color: var(--ink);
  margin: 8px 0 0;
}
.empty {
  text-align: center;
  padding: 40px 0;
  color: var(--muted);
  display: grid;
  gap: 16px;
  justify-items: center;
}
.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  margin-top: 36px;
}
.pager__btn {
  border: 1px solid var(--green);
  background: #fff;
  color: var(--green-dark);
  padding: 10px 20px;
  border-radius: var(--radius-sm);
  font-weight: 700;
  cursor: pointer;
}
.pager__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.pager__info {
  color: var(--muted);
  font-size: 0.9rem;
}
</style>
